/**
 * The call log (docs/plans/oncall-line-plan.md 7.7): one row per call with
 * its status, the time in the SITE's timezone, the length, who answered, and
 * the review state. The rows come unreviewed first (the server's order,
 * callMeta.sortCalls on the client).
 *
 * Mobile first: below `sm` each row is a stacked card; from `sm` up the rows
 * line up under a column header. A row is a link to /oncall/calls/<id>, the
 * same URL the alert emails carry, so opening a call from the list and from
 * an email is one path (the drawer reads the route).
 *
 * The list rows carry no caller identity (plan 16, WP-10): no number, no
 * transcript. Those appear only in the drawer, whose read is audited.
 */
import { Link } from 'react-router-dom';
import { Voicemail } from 'lucide-react';
import type { OncallCallSummary } from '../../api/oncallApi';
import { formatDateTime, formatDuration } from '../../lib/format';
import { CallStatusPill, ReviewPill } from './CallPills';
import { directionMeta } from './callMeta';

interface CallListProps {
  calls: OncallCallSummary[];
  /** The site's IANA timezone (every time shown is the site's wall clock). */
  timeZone: string | null | undefined;
  /** The call open in the drawer, highlighted. */
  selectedId?: string | null;
}

const GRID = 'sm:grid sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_4.5rem_minmax(0,1.3fr)_minmax(0,1fr)] sm:items-center sm:gap-3';

/** Who took the call, in words, for the row. */
export function answeredText(call: OncallCallSummary): string {
  if (call.direction === 'callback') {
    return call.answered_by_name ? `Callback by ${call.answered_by_name}` : 'Callback';
  }
  if (call.answered_by_name) return call.answered_by_name;
  if (call.answered_by_member_id) return 'A team member';
  if (call.status === 'answered') return 'A backup phone';
  if (call.status === 'ringing') return 'Ringing…';
  return 'Nobody answered';
}

export default function CallList({ calls, timeZone, selectedId = null }: CallListProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div
        aria-hidden="true"
        className={`hidden border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400 ${GRID}`}
      >
        <span>Time</span>
        <span>Status</span>
        <span>Length</span>
        <span>Answered by</span>
        <span>Review</span>
      </div>
      <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="oncall-call-list">
        {calls.map((call) => {
          const dir = directionMeta(call.direction);
          const DirIcon = dir.Icon;
          const selected = call.id === selectedId;
          const when = formatDateTime(call.started_at, timeZone);
          return (
            <li key={call.id}>
              <Link
                to={`/oncall/calls/${encodeURIComponent(call.id)}`}
                aria-current={selected ? 'true' : undefined}
                data-testid={`oncall-call-row-${call.id}`}
                className={`flex min-h-[44px] flex-col gap-2 px-4 py-3 text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 ${GRID} ${
                  selected
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <DirIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-gray-900 dark:text-gray-100">{when}</span>
                    {call.direction !== 'inbound' && (
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{dir.label}</span>
                    )}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  <CallStatusPill status={call.status} />
                  {call.has_voicemail && (
                    <span title="A voicemail was left" className="inline-flex items-center text-amber-700 dark:text-amber-400">
                      <Voicemail className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Voicemail left</span>
                    </span>
                  )}
                  {/* Below sm the length and review ride this line. */}
                  <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400 sm:hidden">
                    {formatDuration(call.duration_sec)}
                  </span>
                  <span className="sm:hidden">
                    <ReviewPill review={call.review_status} />
                  </span>
                </span>
                <span className="hidden tabular-nums text-gray-700 dark:text-gray-300 sm:block">
                  {formatDuration(call.duration_sec)}
                </span>
                <span className="truncate text-gray-600 dark:text-gray-300" data-testid="oncall-call-answered">
                  {answeredText(call)}
                </span>
                <span className="hidden sm:block">
                  <ReviewPill review={call.review_status} />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
